import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import type { MachineTarget, MachineView, MissionRunView, MissionView } from "../../shared/contracts";
import { IconCheck, IconPlus, IconX } from "../components/icons";
import { MissionForm, type MissionFormValues } from "../components/mission-form";
import { Badge, Button, Card, cx, ErrorNotice, Eyebrow, Loading, Notice, PageTop, ROUND_BUTTON_PRIMARY, RowList } from "../components/ui";
import { useMachines, useRuns, useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { fmtDayWord, isRunActive, MISSION_STATUS_LABEL, runPlainLine, scheduleWords } from "../lib/format";
import { useDesktop, useNow, usePageTitle } from "../lib/hooks";
import { MissionDetail } from "./mission";
import { useAgentEvents } from "../lib/socket";
import { invalidate, useResource } from "../lib/store";

export function useMissions() {
  return useResource<MissionView[]>("missions", async (signal) => (await api("GET /api/missions", { signal })).missions, {
    refreshOn: ["mission.upsert"],
  });
}

// Shared with the agent's presence (lib/agent.ts); re-exported for the mission page.
export { useRuns };

const MISSION_TONE: Record<string, "success" | "neutral" | "warning" | "accent"> = {
  active: "success",
  paused: "warning",
  done: "accent",
  cancelled: "neutral",
};

export function MissionStatusBadge({ status }: { status: string }) {
  return <Badge tone={MISSION_TONE[status] ?? "neutral"}>{MISSION_STATUS_LABEL[status as MissionView["status"]] ?? status}</Badge>;
}

/** Default machine for new missions: the setting when it still exists, else none. */
export function defaultMachine(setting: MachineTarget | undefined, machines: MachineView[] | undefined): MachineTarget {
  if (!setting || setting === "none") return "none";
  if (setting === "cloud") return "cloud";
  const id = setting.slice("local:".length);
  return machines?.some((m) => m.kind === "local" && m.id === id) ? setting : "none";
}

/** What a mission is up to, in one plain line ("Nova y travaille", "Chaque lundi à 8 h", "Terminée hier"). */
export function missionLine(mission: MissionView, runs: MissionRunView[], agentName: string, now: number): string {
  const active = runs.find((r) => isRunActive(r.status));
  if (active) return runPlainLine(active, agentName, now);
  if (mission.status === "paused") return "En pause";
  if (mission.status === "done") return "Accomplie";
  if (mission.status === "cancelled") return "Abandonnée";
  const schedule = scheduleWords(mission.cadenceMinutes, mission.nextRunAt, now);
  if (schedule) return schedule;
  const last = runs[0];
  return last ? runPlainLine(last, agentName, now) : "Pas encore lancée";
}

/** /missions — what the agent pursues in the background: one line each, the details one tap away. */
export function MissionsPage() {
  usePageTitle("Missions");
  const missions = useMissions();
  const runs = useRuns();
  const machines = useMachines();
  const settings = useSettings();
  const agentName = settings.data?.agentName ?? "Ton agent";
  const now = useNow(60_000);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const desktop = useDesktop();

  useAgentEvents((e) => {
    if (e.type === "mission.upsert") {
      missions.mutate((list) => (list ? (list.some((m) => m.id === e.mission.id) ? list.map((m) => (m.id === e.mission.id ? e.mission : m)) : [e.mission, ...list]) : list));
    } else if (e.type === "run.upsert") {
      runs.mutate((list) => (list ? (list.some((r) => r.id === e.run.id) ? list.map((r) => (r.id === e.run.id ? e.run : r)) : [e.run, ...list]) : list));
    }
  });

  async function create(values: MissionFormValues) {
    try {
      const mission = await api("POST /api/missions", {
        body: { title: values.title, goal: values.goal, cadenceMinutes: values.cadenceMinutes, machine: values.machine, runNow: values.runNow },
      });
      missions.mutate((list) => [mission, ...(list ?? []).filter((m) => m.id !== mission.id)]);
      invalidate("missions");
      invalidate("runs");
      setCreating(false);
      setNotice(`Mission « ${mission.title} » confiée.`);
    } catch (err) {
      throw new Error(errorText(err));
    }
  }

  const allRuns = runs.data ?? [];
  const runsOf = (m: MissionView) => allRuns.filter((r) => r.missionId === m.id);
  const isWorking = (r: MissionRunView[]) => r.some((x) => x.status === "running" || x.status === "queued");
  const isWaiting = (r: MissionRunView[]) => r.some((x) => x.status === "waiting_user" || x.status === "waiting_approval");
  // What waits on the user first, then what works, then the rest.
  const urgency = (m: MissionView) => {
    const r = runsOf(m);
    return isWaiting(r) ? 0 : isWorking(r) ? 1 : 2;
  };
  const all = [...(missions.data ?? [])].sort((a, b) => urgency(a) - urgency(b) || order(a) - order(b) || b.updatedAt - a.updatedAt);
  const finished = (m: MissionView) => m.status === "done" || m.status === "cancelled";
  const regular = (m: MissionView) => !finished(m) && Boolean(m.cadenceMinutes) && urgency(m) === 2;
  const ongoing = all.filter((m) => !finished(m) && !regular(m));
  const regulars = all.filter(regular);
  const done = all.filter(finished);
  const looseRuns = allRuns.filter((r) => !r.missionId).slice(0, 8);
  const looseActive = looseRuns.filter((r) => isRunActive(r.status));
  const looseDone = looseRuns.filter((r) => !isRunActive(r.status)).slice(0, 5);
  const selected = ongoing[0] ?? regulars[0] ?? null;
  const empty = !all.length && !looseRuns.length;

  return (
    <div>
      <PageTop
        title="Missions"
        actions={
          <button type="button" onClick={() => setCreating((c) => !c)} aria-expanded={creating} aria-label="Confier une mission" title="Confier une mission" className={ROUND_BUTTON_PRIMARY}>
            <IconPlus width={16} height={16} strokeWidth={2.4} className={cx("transition-transform", creating && "rotate-45")} />
          </button>
        }
      />

      {creating ? (
        <Card className="cv-in mb-6 max-w-2xl p-4 sm:p-5">
          <MissionForm
            initial={{ title: "", goal: "", cadenceMinutes: null, machine: defaultMachine(settings.data?.defaultMachine, machines.data), runNow: false }}
            machines={machines.data}
            submitLabel="Confier la mission"
            showRunNow
            onSubmit={create}
            onCancel={() => setCreating(false)}
          />
        </Card>
      ) : null}

      {notice ? (
        <Notice tone="neutral" role="status" className="mb-5">
          {notice}
        </Notice>
      ) : null}

      {missions.loading ? (
        <Loading />
      ) : !missions.data ? (
        <ErrorNotice message={missions.error?.message ?? "Impossible de charger les missions."} onRetry={() => void missions.reload()} />
      ) : empty ? (
        !creating ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <p className="text-[15px] text-muted">Aucune mission</p>
            <Button variant="secondary" onClick={() => setCreating(true)}>
              Confier une mission
            </Button>
          </div>
        ) : null
      ) : (
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-2.5">
            {ongoing.length || looseActive.length ? (
              <>
                <Eyebrow>En cours</Eyebrow>
                <ul className="flex flex-col gap-2.5">
                  {ongoing.map((m) => {
                    const r = runsOf(m);
                    return (
                      <MissionCard
                        key={m.id}
                        to={{ mission: m.id }}
                        title={m.title}
                        line={missionLine(m, r, agentName, now)}
                        working={isWorking(r)}
                        waiting={isWaiting(r)}
                        agentName={agentName}
                        current={desktop && selected?.id === m.id}
                      />
                    );
                  })}
                  {looseActive.map((r) => (
                    <MissionCard
                      key={r.id}
                      to={{ run: r.id }}
                      title={r.title}
                      line={runPlainLine(r, agentName, now)}
                      working={r.status === "running" || r.status === "queued"}
                      waiting={r.status === "waiting_user" || r.status === "waiting_approval"}
                      agentName={agentName}
                      current={false}
                    />
                  ))}
                </ul>
              </>
            ) : null}

            {regulars.length ? (
              <>
                <Eyebrow className="pt-3.5">Régulières</Eyebrow>
                <RowList>
                  {regulars.map((m) => (
                    <Link key={m.id} to="/missions/$id" params={{ id: m.id }} data-testid="mission-card" className="flex min-h-11 items-center gap-3 py-[11px] transition hover:opacity-80">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-semibold">{m.title}</span>
                        <span className="block truncate text-[12.5px] text-subtle">{missionLine(m, runsOf(m), agentName, now)}</span>
                      </span>
                      <span className={cx("shrink-0 text-[12.5px]", m.status === "active" ? "text-muted" : "text-subtle")}>{m.status === "active" ? "Active" : "En pause"}</span>
                    </Link>
                  ))}
                </RowList>
              </>
            ) : null}

            {done.length || looseDone.length ? (
              <>
                <Eyebrow className="pt-3.5">Terminées</Eyebrow>
                <ul className="flex flex-col">
                  {[...done.map((m) => ({ key: m.id, to: { mission: m.id }, title: m.title, at: m.updatedAt, ok: m.status === "done" })), ...looseDone.map((r) => ({ key: r.id, to: { run: r.id }, title: r.title, at: r.finishedAt ?? r.createdAt, ok: r.status === "done" }))]
                    .sort((x, y) => y.at - x.at)
                    .slice(0, 8)
                    .map((x) => (
                      <li key={x.key} data-testid="mission-card">
                        <RowLink to={x.to} className="flex min-h-11 items-center gap-3 rounded-xl px-1 py-2 transition hover:bg-elev">
                          <span aria-hidden="true" className={cx("flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full", x.ok ? "bg-ink text-ink-fg" : "bg-chip text-muted")}>
                            {x.ok ? <IconCheck width={11} height={11} strokeWidth={3.4} /> : <IconX width={11} height={11} strokeWidth={3.4} />}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[14.5px]">{x.title}</span>
                          <span className="shrink-0 text-[12.5px] text-subtle">{fmtDayWord(x.at, now)}</span>
                        </RowLink>
                      </li>
                    ))}
                </ul>
              </>
            ) : null}
          </div>

          {desktop && selected ? (
            <div className="min-w-0 flex-[999_1_480px]">
              <MissionDetail id={selected.id} embedded />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function order(m: MissionView): number {
  return m.status === "active" ? 0 : m.status === "paused" ? 1 : 2;
}

function RowLink({ to, className, children }: { to: { mission: string } | { run: string }; className?: string; children: ReactNode }) {
  return "mission" in to ? (
    <Link to="/missions/$id" params={{ id: to.mission }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/runs/$id" params={{ id: to.run }} className={className}>
      {children}
    </Link>
  );
}

/** One mission (or run) under « En cours » (data-testid="mission-card"): its state, its title, a thin bar while it works. */
function MissionCard({
  to,
  title,
  line,
  working,
  waiting,
  agentName,
  current,
}: {
  to: { mission: string } | { run: string };
  title: string;
  line: string;
  working: boolean;
  waiting: boolean;
  agentName: string;
  current: boolean;
}) {
  return (
    <li data-testid="mission-card">
      <RowLink
        to={to}
        className={cx(
          "block rounded-[18px] bg-elev p-4 transition hover:bg-sunken",
          current && "shadow-[inset_0_0_0_1px_var(--fg-subtle)]",
        )}
      >
        {working || waiting ? (
          <span className={cx("flex items-center gap-2 text-[12.5px] font-semibold", waiting ? "text-accent" : "text-muted")}>
            <span aria-hidden="true" className={cx("h-[7px] w-[7px] rounded-full bg-accent", !waiting && "live-dot")} />
            {waiting ? "Une question pour toi" : `${agentName} y travaille`}
          </span>
        ) : null}
        <span className={cx("block text-[15.5px] font-semibold wrap-anywhere", (working || waiting) && "mt-1.5")}>{title}</span>
        {working ? <span aria-hidden="true" className="progress-glide mt-3 block" /> : <span className="mt-0.5 block truncate text-[12.5px] text-subtle">{line}</span>}
      </RowLink>
    </li>
  );
}
